/*
 * Copyright 2024 NewMedia Centre - Delft University of Technology
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

'use server'

// import dependencies
import { z } from 'zod'
import { webAppEditProcedure, webAppProcedure } from './procedures'
import { PutObjectCommand, DeleteObjectCommand } from '@aws-sdk/client-s3'
import { s3Client } from '@/db/s3'
import { prisma } from '@/db'
import env from '@/env'

/** Changes the icon for the web app */
export const uploadIconAction = webAppEditProcedure.createServerAction()
	.input(
		z.object({
			icon: z
				.instanceof(File)
				.refine(
					file => file.size > 0 && file.size < 1024 * 1024,
					'File size must be less than 1MB'
				).refine(
					file => file.type.startsWith('image/'),
					'File must be an image'
				)
		}),
		{
			type: 'state'
		}
	)
	.output(z.object({
		webAppId: z.string(),
		mime: z.string()
	}))
	.handler(async ({ ctx, input }) => {
		const data = await input.icon.arrayBuffer()

		// set the file as the web app icon
		const icon = await prisma.webAppIcon.upsert({
			where: {
				webAppId: input.id
			},
			create: {
				mime: input.icon.type,
				webApp: {
					connect: {
						id: input.id,
					}
				}
			},
			update: {
				mime: input.icon.type,
			}
		})

		const command = new PutObjectCommand({
			Bucket: env.S3_ICONS_BUCKET,
			Key: `${input.id}`,
			Body: Buffer.from(data),
			ContentType: input.icon.type,
			Metadata: {
				'webapp': ctx.webApp.id,
				'webapp-slug': ctx.webApp.slug,
			}
		})

		try {
			await s3Client.send(command)
		} catch {
			// if the upload fails, delete the icon from the database
			await prisma.webAppIcon.delete({
				where: {
					webAppId: icon.webAppId,
				}
			})
			throw new Error('Failed to upload icon')
		}

		return icon
	})

/** Gets all media for the web app */
export const getMediaAction = webAppProcedure.createServerAction()
	.input(z.object({
		hero: z.boolean().optional()
	}))
	.output(z.array(z.object({
		id: z.string(),
		webAppId: z.string(),
		hero: z.boolean(),
		mime: z.string()
	})))
	.handler(async ({ input }) => {
		return await prisma.webAppMedia.findMany({
			where: {
				webAppId: input.id,
				...(input.hero ? { hero: input.hero } : {})
			}
		})
	})

/** Uploads media for the web app */
export const uploadMediaAction = webAppEditProcedure.createServerAction()
	.input(
		z.object({
			media: z
				.instanceof(File)
				.refine(
					file => file.type.startsWith('image/') || file.type.startsWith('video/'),
					'File must be an image or a video',
				)
				.refine(
					file => file.type.startsWith('video/') ? s3Client && env.S3_MEDIA_BUCKET : true,
					'Video files are only supported when using S3'
				)
				.refine(
					file => file.type.startsWith('image/') ? file.size > 0 && file.size < 10 * 1024 * 1024 : file.size > 0,
					'File size must be less than 10MB'
				),
			hero: z.boolean().default(false)
		}),
		{
			type: 'state'
		}
	)
	.output(z.object({
		id: z.string(),
		webAppId: z.string(),
		hero: z.boolean(),
		mime: z.string()
	}))
	.handler(async ({ ctx, input }) => {
		const data = await input.media.arrayBuffer()

		// set the file as the web app hero
		const media = await prisma.webAppMedia.create({
			data: {
				mime: input.media.type,
				hero: input.hero,
				webApp: {
					connect: {
						id: input.id,
					}
				}
			}
		})

		const command = new PutObjectCommand({
			Bucket: env.S3_MEDIA_BUCKET,
			Key: `${input.id}/${media.id}`,
			Body: Buffer.from(data),
			ContentType: input.media.type,
			Metadata: {
				'webapp': ctx.webApp.id,
				'webapp-slug': ctx.webApp.slug,
			}
		})

		try {
			await s3Client.send(command)
		} catch {
			// if the upload fails, delete the icon from the database
			await prisma.webAppMedia.delete({
				where: {
					id: media.id,
				}
			})
			throw new Error('Failed to upload icon')
		}

		return media
	})

export const removeMediaAction = webAppEditProcedure.createServerAction()
	.input(z.object({
		mediaId: z.string().uuid()
	}))
	.handler(async ({ input }) => {
		const media = await prisma.webAppMedia.delete({
			where: {
				id: input.mediaId,
				webAppId: input.id,
			}
		})

		// delete the media from s3
		if (s3Client && env.S3_MEDIA_BUCKET) {
			const command = new DeleteObjectCommand({
				Bucket: env.S3_MEDIA_BUCKET,
				Key: `${input.id}/${media.id}`
			})
			await s3Client.send(command)
		}
	})